import React, { useState } from "react";
import { AlertCircle, CalendarClock, Clock, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Card, CardContent } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { Label } from "@pgs-core/ui/components/label";
import { Checkbox } from "@pgs-core/ui/components/checkbox";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@pgs-core/ui/components/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@pgs-core/ui/components/alert-dialog";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { BookingModuleGuard, BookingModuleHeader } from "../components/booking-module-shell";
import { WeeklyHoursGrid } from "../components/weekly-hours-grid";
import {
  createService,
  deleteService,
  fetchServiceAvailability,
  fetchServices,
  formatDuration,
  formatServicePrice,
  saveServiceAvailability,
  updateService,
  type AvailabilityRule,
  type PortalService,
} from "../lib/booking";
import { parseOpeningHours, weekToRules, type WeeklyHours } from "../lib/opening-hours";
import type { ClientOutletContext } from "../lib/outlet-context";

interface ServiceForm {
  name: string;
  description: string;
  duration: string;
  buffer: string;
  price: string;
  capacity: string;
  deposit: boolean;
  active: boolean;
}

const EMPTY_FORM: ServiceForm = {
  name: "", description: "", duration: "60", buffer: "0", price: "", capacity: "1",
  deposit: false, active: true,
};

function toForm(service: PortalService): ServiceForm {
  return {
    name: service.name,
    description: service.description ?? "",
    duration: String(service.duration_minutes),
    buffer: String(service.buffer_minutes),
    price: service.price_pence != null ? (service.price_pence / 100).toFixed(2) : "",
    capacity: String(service.capacity),
    deposit: service.deposit_required === 1,
    active: service.active === 1,
  };
}

/** Form → API fields; returns an error message for unparseable numbers. */
function fromForm(form: ServiceForm): { fields: Partial<PortalService> } | { error: string } {
  const duration = Number(form.duration);
  const buffer = Number(form.buffer);
  const capacity = Number(form.capacity);
  if (!Number.isInteger(duration) || duration < 5) return { error: "Duration must be at least 5 minutes." };
  if (!Number.isInteger(buffer) || buffer < 0) return { error: "Buffer must be 0 or more minutes." };
  if (!Number.isInteger(capacity) || capacity < 1) return { error: "Capacity must be at least 1." };

  let price_pence: number | null = null;
  const priceText = form.price.trim().replace(/^£/, "");
  if (priceText) {
    const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(priceText);
    if (!match) return { error: "Price must look like 12.50 (or be empty)." };
    price_pence = Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0") || "0");
  }

  return {
    fields: {
      name: form.name.trim(),
      description: form.description.trim() || null,
      duration_minutes: duration,
      buffer_minutes: buffer,
      price_pence,
      capacity,
      deposit_required: form.deposit ? 1 : 0,
      active: form.active ? 1 : 0,
    },
  };
}

function Services({ api }: ClientOutletContext) {
  const query = useApiQuery(() => (api ? fetchServices(api) : null), [api]);
  const services = query.data?.services ?? null;

  const [editing, setEditing] = useState<PortalService | "new" | null>(null);
  const [form, setForm] = useState<ServiceForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<PortalService | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  // Per-service bookable hours dialog (availability is per booking type).
  const [hoursTarget, setHoursTarget] = useState<PortalService | null>(null);
  const [hoursRules, setHoursRules] = useState<AvailabilityRule[] | null>(null);
  const [openingWeek, setOpeningWeek] = useState<WeeklyHours | null>(null);
  const [hoursSaving, setHoursSaving] = useState(false);
  const [hoursError, setHoursError] = useState("");

  const openHours = async (service: PortalService) => {
    if (!api) return;
    setHoursTarget(service);
    setHoursRules(null);
    setHoursError("");
    try {
      const [availability, config] = await Promise.all([
        fetchServiceAvailability(api, service.id),
        api.request<{ config: { opening_hours_json?: string | null } | null }>("/tenant/config"),
      ]);
      setHoursRules(availability.rules);
      setOpeningWeek(parseOpeningHours(config.config?.opening_hours_json));
    } catch (err) {
      setHoursError((err as Error).message);
    }
  };

  const saveHours = async () => {
    if (!api || !hoursTarget || !hoursRules) return;
    for (const rule of hoursRules) {
      if (!rule.start_time || !rule.end_time || rule.end_time <= rule.start_time) {
        setHoursError("Every window needs a start time before its end time.");
        return;
      }
    }
    setHoursSaving(true);
    setHoursError("");
    try {
      await saveServiceAvailability(api, hoursTarget.id, hoursRules);
      setHoursTarget(null);
    } catch (err) {
      setHoursError((err as Error).message);
    } finally {
      setHoursSaving(false);
    }
  };

  const open = (target: PortalService | "new") => {
    setForm(target === "new" ? EMPTY_FORM : toForm(target));
    setFormError("");
    setEditing(target);
  };

  const save = async () => {
    if (!api || !editing) return;
    if (!form.name.trim()) {
      setFormError("Enter a service name.");
      return;
    }
    const parsed = fromForm(form);
    if ("error" in parsed) {
      setFormError(parsed.error);
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      if (editing === "new") await createService(api, parsed.fields);
      else await updateService(api, editing.id, parsed.fields);
      setEditing(null);
      query.reload();
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!api || !deleteTarget) return;
    setDeleting(true);
    setError("");
    try {
      await deleteService(api, deleteTarget.id);
      setDeleteTarget(null);
      query.reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const set = (patch: Partial<ServiceForm>) => setForm((current) => ({ ...current, ...patch }));

  return (
    <div>
      <BookingModuleHeader
        action={
          <Button size="sm" onClick={() => open("new")}>
            <Plus className="h-4 w-4" />
            New service
          </Button>
        }
      />

      {(error || query.error) && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error || query.error}</AlertDescription>
        </Alert>
      )}

      {!services && !query.error && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {services && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Service</th>
                  <th className="px-4 py-3 font-medium">Duration</th>
                  <th className="px-4 py-3 font-medium">Price</th>
                  <th className="px-4 py-3 font-medium">Capacity</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {services.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                      No services yet — add what customers can book (a table, a treatment, a fitting…).
                    </td>
                  </tr>
                )}
                {services.map((service) => (
                  <tr key={service.id} className="border-b last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="font-medium">{service.name}</div>
                      {service.description && (
                        <div className="text-xs text-muted-foreground">{service.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDuration(service.duration_minutes)}
                      {service.buffer_minutes > 0 && ` +${service.buffer_minutes}m gap`}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatServicePrice(service.price_pence)}
                      {service.deposit_required === 1 && (
                        <Badge variant="secondary" className="ml-2">Deposit</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{service.capacity}</td>
                    <td className="px-4 py-3">
                      <Badge variant={service.active === 1 ? "success" : "secondary"}>
                        {service.active === 1 ? "Bookable" : "Hidden"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { void openHours(service); }}>
                          <Clock className="h-3.5 w-3.5" />
                          Hours
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => open(service)}>
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          title="Delete service"
                          onClick={() => setDeleteTarget(service)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!editing} onOpenChange={(isOpen: boolean) => !isOpen && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New service" : "Edit service"}</DialogTitle>
            <DialogDescription>
              What customers can book, how long it takes, and how many bookings fit each slot.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="svc-name">Name</Label>
              <Input id="svc-name" value={form.name} autoFocus placeholder="e.g. Table for dinner"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="svc-desc">Description (optional)</Label>
              <Input id="svc-desc" value={form.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="svc-duration">Duration (minutes)</Label>
                <Input id="svc-duration" type="number" min={5} value={form.duration}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ duration: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="svc-buffer">Gap after (minutes)</Label>
                <Input id="svc-buffer" type="number" min={0} value={form.buffer}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ buffer: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="svc-price">Price £ (optional)</Label>
                <Input id="svc-price" inputMode="decimal" placeholder="e.g. 25.00" value={form.price}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ price: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="svc-capacity">Bookings per slot</Label>
                <Input id="svc-capacity" type="number" min={1} value={form.capacity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ capacity: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.deposit} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ deposit: e.target.checked })} />
              Deposit required (shown to customers — collected offline for now)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.active} onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ active: e.target.checked })} />
              Bookable on the website
            </label>
            {formError && <p className="text-sm text-destructive">{formError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={() => { void save(); }} disabled={saving || !form.name.trim()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {editing === "new" ? "Create service" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-service bookable hours */}
      <Dialog open={!!hoursTarget} onOpenChange={(isOpen: boolean) => !isOpen && setHoursTarget(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bookable hours — {hoursTarget?.name}</DialogTitle>
            <DialogDescription>
              When this service can be booked. Hours outside your opening hours are allowed —
              they're just flagged so late finishes or early starts are deliberate.
            </DialogDescription>
          </DialogHeader>
          {!hoursRules && !hoursError && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {hoursRules && (
            <div className="space-y-3">
              <Button
                variant="outline" size="sm"
                onClick={() => setHoursRules(weekToRules(openingWeek ?? {}))}
              >
                <CalendarClock className="h-3.5 w-3.5" />
                Use opening hours
              </Button>
              <WeeklyHoursGrid
                rules={hoursRules}
                onChange={setHoursRules}
                openingHours={openingWeek}
                closedLabel="Not bookable"
              />
            </div>
          )}
          {hoursError && <p className="text-sm text-destructive">{hoursError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoursTarget(null)} disabled={hoursSaving}>Cancel</Button>
            <Button onClick={() => { void saveHours(); }} disabled={hoursSaving || !hoursRules}>
              {hoursSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}
              Save hours
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(isOpen: boolean) => !isOpen && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Customers won't be able to book it any more. Existing bookings are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e: React.MouseEvent) => { e.preventDefault(); void remove(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete service
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function BookingServicesPage() {
  return (
    <BookingModuleGuard title="Bookings" description="The services customers can book.">
      {(ctx) => <Services {...ctx} />}
    </BookingModuleGuard>
  );
}
